import { useEffect, useRef } from 'react';

const IDLE_VALUE = 0.035;
const MAX_HEIGHT = 0.8;
const SCROLL_SPEED = 50;
const VISUAL_SMOOTHING = 0.32;

const AudioTimeline = ({ active = false, barGap = 1, barWidth = 2, height = 40, sensitivity = 1.7, stream }) => {
	const canvasRef = useRef(null);

	useEffect(() => {
		const canvas = canvasRef.current;
		const context = canvas.getContext('2d');
		const rect = canvas.getBoundingClientRect();
		const ratio = window.devicePixelRatio || 1;
		const color = window.getComputedStyle(canvas).color;

		canvas.width = rect.width * ratio;
		canvas.height = height * ratio;
		context.scale(ratio, ratio);
		context.clearRect(0, 0, rect.width, height);
		context.fillStyle = color;

		if (!active || !stream) {
			const count = Math.floor(rect.width / (barWidth + barGap));

			drawBars(context, new Array(count).fill(IDLE_VALUE), barWidth, barGap, height);
			return;
		}

		const audioContext = new AudioContext();
		const analyser = audioContext.createAnalyser();
		const source = audioContext.createMediaStreamSource(stream);
		let visibleData = new Array(getBarCount(rect.width, barWidth, barGap) + 2).fill(IDLE_VALUE);
		let animationFrame = null;
		let scrollOffset = 0;
		let previousTime = performance.now();
		let visibleValue = IDLE_VALUE;

		analyser.fftSize = 256;
		analyser.smoothingTimeConstant = 0.9;
		const frequencyData = new Uint8Array(analyser.frequencyBinCount);
		source.connect(analyser);

		const draw = time => {
			const width = canvas.getBoundingClientRect().width;
			const pitch = barWidth + barGap;
			const barCount = getBarCount(width, barWidth, barGap) + 2;
			const delta = Math.min(100, time - previousTime);

			previousTime = time;

			analyser.getByteFrequencyData(frequencyData);
			visibleValue = smoothValue(visibleValue, formatMicrophoneValue(frequencyData, sensitivity));
			scrollOffset += (delta / 1000) * SCROLL_SPEED;

			while (scrollOffset >= pitch) {
				scrollOffset -= pitch;
				visibleData = [...visibleData.slice(1), visibleValue];
			}

			if (visibleData.length !== barCount) {
				visibleData = fitVisibleData(visibleData, barCount);
			}

			context.clearRect(0, 0, width, height);
			drawScrollingBars(context, visibleData, barWidth, barGap, height, width, scrollOffset);

			animationFrame = window.requestAnimationFrame(draw);
		};

		animationFrame = window.requestAnimationFrame(draw);

		return () => {
			if (animationFrame) window.cancelAnimationFrame(animationFrame);
			source.disconnect();
			audioContext.close();
		};
	}, [active, barGap, barWidth, height, sensitivity, stream]);

	return (
		<div className='min-w-0 flex-1 overflow-hidden text-foreground-body'>
			<canvas ref={canvasRef} className='block h-10 w-full' />
		</div>
	);
};

const getBarCount = (width, barWidth, barGap) => Math.ceil(width / (barWidth + barGap));

const fitVisibleData = (data, count) => {
	if (data.length > count) return data.slice(data.length - count);
	return [...new Array(count - data.length).fill(IDLE_VALUE), ...data];
};

const drawBars = (context, data, barWidth, barGap, height, count = data.length) => {
	Array.from({ length: count }).forEach((_, index) => {
		const dataIndex = Math.floor((index / count) * data.length);
		const value = Math.max(IDLE_VALUE, data[dataIndex] || 0);
		const barHeight = Math.max(2, value * height * MAX_HEIGHT);
		const x = index * (barWidth + barGap);
		const y = (height - barHeight) / 2;
		drawBar(context, x, y, barWidth, barHeight);
	});
};

const drawScrollingBars = (context, data, barWidth, barGap, height, width, scrollOffset) => {
	const pitch = barWidth + barGap;
	data.forEach((value, index) => {
		const x = width - (data.length - index) * pitch - scrollOffset;
		if (x < -barWidth || x > width) return;
		const barHeight = Math.max(2, Math.max(IDLE_VALUE, value || 0) * height * MAX_HEIGHT);
		const y = (height - barHeight) / 2;
		drawBar(context, x, y, barWidth, barHeight);
	});
};

const drawBar = (context, x, y, width, height) => {
	const radius = Math.min(width / 2, height / 2);
	if (context.roundRect) {
		context.beginPath();
		context.roundRect(x, y, width, height, radius);
		context.fill();
		return;
	}
	context.fillRect(x, y, width, height);
};

const smoothValue = (previous, value) => previous + (value - previous) * VISUAL_SMOOTHING;

const formatMicrophoneValue = (data, sensitivity) => {
	const start = Math.floor(data.length * 0.05);
	const end = Math.floor(data.length * 0.4);
	const relevant = data.slice(start, end);
	const sum = relevant.reduce((total, value) => total + value, 0);
	const average = sum / Math.max(1, relevant.length);
	const normalized = (average / 255) * sensitivity;

	return Math.min(1, Math.max(IDLE_VALUE, normalized));
};

export { AudioTimeline };
